import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Image as ImageIcon, Upload, Trash2, Save, AlertCircle, CheckCircle2 } from "lucide-react";

export default function ProfilePage() {
  const { user, refresh } = useAuth();
  const [leagueName, setLeagueName] = useState(user?.league_name || "");
  const [logo, setLogo] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/profile/logo").then(({ data }) => setLogo(data.logo_base64 || "")).catch(() => {});
  }, []);

  const onSaveLeague = async () => {
    setSaving(true);
    setError("");
    setInfo("");
    try {
      await api.put("/profile/league-name", { league_name: leagueName });
      await refresh();
      setInfo("Nome lega aggiornato.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onUploadLogo = async (file) => {
    if (!file) return;
    setError("");
    setInfo("");
    setSaving(true);
    try {
      const form = new FormData();
      form.append("logo", file);
      await api.post("/profile/logo", form, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.get("/profile/logo");
      setLogo(data.logo_base64 || "");
      await refresh();
      setInfo("Logo aggiornato.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onDeleteLogo = async () => {
    if (!window.confirm("Eliminare il logo?")) return;
    setSaving(true);
    try {
      await api.delete("/profile/logo");
      setLogo("");
      await refresh();
      setInfo("Logo rimosso.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="mb-8">
        <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-2">[ Profilo Steward ]</div>
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
          Account & <span className="text-yellow-500">Lega</span>
        </h1>
      </div>

      <div className="card mb-6">
        <div className="section-h">Identità</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-zinc-500 font-mono-tel uppercase">Nome</label>
            <div className="font-display font-bold text-lg text-white">{user?.name}</div>
          </div>
          <div>
            <label className="text-xs text-zinc-500 font-mono-tel uppercase">Email</label>
            <div className="font-mono-tel text-zinc-300">{user?.email}</div>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="section-h">Nome della Lega</div>
        <p className="text-zinc-500 text-sm mb-3">Apparirà nell'intestazione dei PDF dei verdetti.</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={leagueName}
            onChange={(e) => setLeagueName(e.target.value)}
            placeholder="Es. Apex Racing League"
            className="input-field"
            data-testid="league-name-input"
            maxLength={80}
          />
          <button onClick={onSaveLeague} disabled={saving} className="btn-primary" data-testid="save-league-button">
            <Save className="w-4 h-4" /> Salva
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="section-h">Logo della Lega (PDF)</div>
        <p className="text-zinc-500 text-sm mb-3">PNG, JPEG o WEBP · max 2MB · sarà ridimensionato a 400px lato lungo.</p>
        <div className="flex items-start gap-4">
          <div className="w-32 h-32 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden">
            {logo ? (
              <img src={`data:image/png;base64,${logo}`} alt="logo" className="max-w-full max-h-full object-contain" data-testid="current-logo" />
            ) : (
              <ImageIcon className="w-10 h-10 text-zinc-700" />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
              onChange={(e) => onUploadLogo(e.target.files?.[0])} data-testid="logo-file-input" />
            <button onClick={() => inputRef.current?.click()} disabled={saving} className="btn-primary" data-testid="upload-logo-button">
              <Upload className="w-4 h-4" /> Carica logo
            </button>
            {logo && (
              <button onClick={onDeleteLogo} disabled={saving} className="btn-danger" data-testid="delete-logo-button">
                <Trash2 className="w-4 h-4" /> Rimuovi
              </button>
            )}
          </div>
        </div>
      </div>

      {info && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm p-3 flex gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-none" /> {info}
        </div>
      )}
      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3 flex gap-2" data-testid="profile-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-none" /> {error}
        </div>
      )}
    </div>
  );
}
